import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { listPtProgrammes, listSptInterest, updateSptInterest } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, AlertTriangle, Eye, Copy } from "lucide-react";
import { AssignDialog } from "@/pages/PtProgrammeBuilder";
import WorkoutCalendar from "@/components/WorkoutCalendar";

const TABS = [
  { value: "", label: "All" },
  { value: "group", label: "Daily workouts" },
  { value: "PT", label: "PT" },
  { value: "SPT", label: "SPT" },
  { value: "template", label: "Templates" },
  { value: "calendar", label: "Calendar" },
];
const STATE = {
  running: "text-emerald-300",
  upcoming: "text-sky-300",
  ended: "text-zinc-500",
};
const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—");

export default function PtProgrammes() {
  const { activeFacility } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [tab, setTab] = useState("");
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState("");
  const [assigning, setAssigning] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await listPtProgrammes(mine ? { mine: true } : {});
      setRows(d.programmes);
      setCanEdit(d.can_edit);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load programmes");
      setRows([]);
    }
  }, [mine]);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const shown = useMemo(() => {
    const k = q.trim().toLowerCase();
    return (rows || []).filter((p) => (!tab || p.kind === tab) &&
      (!k || `${p.name} ${p.trainer_name || ""} ${p.clients.map((c) => c.name).join(" ")}`.toLowerCase().includes(k)));
  }, [rows, tab, q]);
  const endingSoon = (rows || []).filter((p) => p.ending_soon);

  return (
    <div className="space-y-6" data-testid="pt-programmes">
      <SptInterest />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Member App · Programming</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Programmes</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            One builder for the gym&apos;s daily workouts (everyone at the gym sees each day as the WOD), PT programmes and SPT groups.
            Write week 1, set how each exercise progresses, and pick the number of weeks. Only the programming team can edit.
            Templates are shared by every gym.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/member-app/programmes/new?kind=template")} data-testid="programme-new-template">
              <Plus className="mr-1 h-4 w-4" /> New template
            </Button>
            <Button onClick={() => navigate("/member-app/programmes/new")} data-testid="programme-new">
              <Plus className="mr-1 h-4 w-4" /> New programme
            </Button>
          </div>
        )}
      </div>

      {endingSoon.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4" data-testid="programmes-ending-soon">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-300">
            <AlertTriangle className="h-4 w-4" /> Ending in the next two weeks: time to talk renewal
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {endingSoon.map((p) => (
              <button key={p.id} type="button" onClick={() => navigate(`/member-app/programmes/${p.id}`)}
                className="rounded-full border border-zinc-700 px-3 py-1 text-xs hover:border-amber-400 hover:text-amber-200">
                {p.clients.map((c) => c.name).join(", ")} · {p.name} · ends {fmt(p.end_date)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-md border border-zinc-800 p-0.5">
          {TABS.map((t) => (
            <button key={t.value} type="button" onClick={() => setTab(t.value)}
              className={`rounded px-3 py-1 text-xs ${tab === t.value ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab !== "calendar" && (
          <>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search programme, client or trainer" className="max-w-xs" />
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Only my clients
            </label>
          </>
        )}
      </div>

      {tab === "calendar" && <WorkoutCalendar />}

      {!canEdit && rows && (
        <div className="flex items-center gap-2 text-xs text-zinc-500"><Eye className="h-3.5 w-3.5" /> View only. The programming team writes programmes.</div>
      )}

      {tab === "calendar" ? null : rows === null ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500">
          {rows.length ? "Nothing matches." : "No programmes yet. Start with a template, then assign it to clients."}
        </div>
      ) : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="programmes-table">
            <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <tr className="border-b border-[#1f1f1f]">
                <th className="px-4 py-3">Programme</th><th className="px-4 py-3">Clients</th><th className="px-4 py-3">Trainer</th>
                <th className="px-4 py-3">Length</th><th className="px-4 py-3">Dates</th><th className="px-4 py-3">Done</th><th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} onClick={() => navigate(`/member-app/programmes/${p.id}`)}
                  className="cursor-pointer border-b border-[#161616] hover:bg-white/[0.02]" data-testid={`programme-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-zinc-100">{p.name}</div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{p.kind === "template" ? "Template" : p.kind === "group" ? "Daily workout" : p.kind}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{p.clients.map((c) => c.name).join(", ") || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3 text-zinc-300">{p.trainer_name || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3 text-zinc-400">{p.weeks} wk · {p.days_per_week}×/wk</td>
                  <td className="px-4 py-3 text-zinc-400">
                    {p.kind === "template" ? "—" : <>{fmt(p.start_date)} – {fmt(p.end_date)} <span className={`ml-1 text-[10px] uppercase ${STATE[p.state]}`}>{p.state}</span></>}
                  </td>
                  <td className="px-4 py-3 text-zinc-300 tabular-nums">
                    {p.kind === "template" ? "—" : p.kind === "group" ? "Whole gym" : p.clients.map((c) => `${p.clients.length > 1 ? c.name.split(" ")[0] + " " : ""}${p.done?.[c.id] ?? 0}/${p.planned}`).join(" · ")}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {canEdit && p.kind === "template" && (
                      <Button size="sm" variant="outline" onClick={() => setAssigning(p)} data-testid={`programme-assign-${p.id}`}>
                        <Copy className="mr-1 h-3.5 w-3.5" /> Assign
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {assigning && (
        <AssignDialog source={assigning} onClose={() => setAssigning(null)}
          onDone={(p) => navigate(`/member-app/programmes/${p.id}`)} />
      )}
    </div>
  );
}


const INTEREST_STATUS = [["contacted", "Contacted"], ["joined", "Joined"], ["not_now", "Not now"]];

/** Members who tapped "I'm interested" on a semi-private training group in the app. The team calls
 *  them and marks the outcome. Shown only when there is someone to call or recently called. */
function SptInterest() {
  const { activeFacility } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => listSptInterest().then(setData).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);
  if (!data || !data.interest.length) return null;
  const mark = async (id, status) => {
    try { await updateSptInterest(id, status); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");
  return (
    <div className="card-surface p-4 space-y-3" data-testid="spt-interest">
      <div className="flex items-center justify-between">
        <div className="eyebrow">Interested in semi-private training</div>
        {data.new ? <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-300">{data.new} to call</span> : null}
      </div>
      <ul className="divide-y divide-[#1c1c1c]">
        {data.interest.slice(0, 20).map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2 text-sm">
            <div className="min-w-[12rem] flex-1">
              <div className="font-medium text-zinc-100">{r.client_name || "Member"} <span className="text-zinc-500 font-normal">{r.phone || ""}</span></div>
              <div className="text-xs text-zinc-500">{r.programme_name || "Semi-private training"}{r.start_date ? ` · starts ${fmt(r.start_date)}` : ""} · asked {fmt(r.created_at)}</div>
            </div>
            {r.status === "new" ? (
              <div className="flex gap-1.5">
                {INTEREST_STATUS.map(([k, l]) => (
                  <Button key={k} size="sm" variant="outline" className="h-7 text-xs" onClick={() => mark(r.id, k)}>{l}</Button>
                ))}
              </div>
            ) : <span className="text-xs text-zinc-400">{r.status_label}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
